/**
 * Reconciles server-stored "the user wants this on" preferences against the
 * OS's actual current permission grant.
 *
 * Both Smart Auto-Release and Push Notifications persist an enabled flag
 * server-side (tied to the account), independent of any one device install.
 * A fresh APK install (or the OS permission being revoked in device
 * Settings) resets the OS grant to ungranted, but the server still says
 * "enabled: true" — so the UI toggle rendered ON from stored state alone,
 * while the underlying native listener/token was never actually armed.
 * These functions call the native `checkPermissions()` (a read-only query,
 * no OS prompt) and self-heal the stored flag when it disagrees with
 * reality. Call on every app launch AND whenever the settings page mounts,
 * since permission can also be revoked while the app is merely backgrounded.
 */
import { getLocationAutomation, updateLocationAutomation } from '../api/users';
import { autoReleasePlugin } from './autoReleasePlugin';
import { isNativePlatform } from './runtime';
import { useAuthStore } from '../store/authStore';

export async function reconcileAutoReleasePermission(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { data: settings } = await getLocationAutomation().then((r) => r.data);
    if (!settings.releaseEnabled) return;

    const { state } = await autoReleasePlugin.checkPermissions();
    if (state === 'granted') return; // still good — nothing to do

    // Permission was revoked (or never actually granted on this install)
    // since the flag was last set. Disarm defensively (idempotent even if
    // it was never armed) and flip the stored flag off so the UI stops
    // showing a toggle that isn't backed by a working listener.
    await autoReleasePlugin.disarm().catch(() => {});
    await updateLocationAutomation({ releaseEnabled: false });
  } catch (err) {
    console.warn('reconcileAutoReleasePermission failed', err);
  }
}

interface CapacitorPushPlugin {
  checkPermissions: () => Promise<{ receive: string }>;
}

export async function reconcilePushPermission(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { isPushNotificationsEnabled, setPushToken, setIsPushNotificationsEnabled } = useAuthStore.getState();
    if (!isPushNotificationsEnabled) return;

    const PushNotifications = (globalThis as any).Capacitor?.Plugins?.PushNotifications as CapacitorPushPlugin | undefined;
    if (!PushNotifications) return;

    const { receive } = await PushNotifications.checkPermissions();
    if (receive === 'granted') return;

    // Same self-heal as auto-release: the account-level flag says "on", the
    // OS disagrees, so correct local state to match. Server-side
    // `notification_preferences.master` is left untouched — it's the user's
    // stated preference, not a device grant, and re-enabling should be one
    // tap without re-asking "which categories do you want."
    setPushToken(null);
    setIsPushNotificationsEnabled(false);
  } catch (err) {
    console.warn('reconcilePushPermission failed', err);
  }
}
